import { useMutation } from '@apollo/client';
import type { UpdateControlTestInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateControlTestInternalAuditResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateControlTestInternalAuditResultInput } from './useUpdateControlTestInternalAuditResultTRPC';
import { useUpdateControlTestInternalAuditResultTRPC } from './useUpdateControlTestInternalAuditResultTRPC';

export const useUpdateControlTestInternalAuditResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateControlTestInternalAuditResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'control_test_internal_audit_result');
        evictField(cache, 'control');
        evictField(cache, 'internal_audit_report');
      },
    }
  );

  const trpcMutation = useUpdateControlTestInternalAuditResultTRPC();

  const updateControlTestInternalAuditResult = async (
    variables: UpdateControlTestInternalAuditResultInput
  ): Promise<UpdateControlTestInternalAuditResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateControlTestInternalAuditResult(variables);
    }

    const result = await updateGraphQL({ variables: { object: variables } });
    if (!result.data) {
      throw new Error('Failed to update control test internal audit result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateControlTestInternalAuditResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateControlTestInternalAuditResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
